import React, {useContext, useRef, useState, useEffect} from 'react'
import {useNavigate} from 'react-router-dom'
import AppContext from '../../appContext'
import Stepper, {StepperRef} from '../../components/Stepper'
import {bind, onEnter, wrapLoader} from '../../wrapper'
import PagedSearchTable, {PagedTableFunctions} from '../../components/PagedSearchTable'
import ClientController from '../../controllers/ClientController'
import ClientPaged from '../../controllers/ClientPaged'
import LimeController from '../../controllers/LimeController'
import LimeTemplateBasicResult from '../../controllers/LimeTemplateBasicResult'
import LimeProductResponse from '../../controllers/LimeProductResponse'
import LimeProductType from '../../controllers/LimeProductType'
import LimeProgramProductSources from '../../controllers/LimeProgramProductSources'
import LimeProgramLabInputs from '../../controllers/LimeProgramLabInputs'
import LimingRegionResponse from '../../controllers/LimingRegionResponse'

import LandResponse from "../../controllers/LandResponse"
import Dialog from '../../components/Dialog'

import LimeSoilDataDialog from './LimeSoilDataDialog'
import Failed from '../../components/Failed'
import CheckBox from '../../components/CheckBox'
import {calculateLandRows, buildProductValues, CalcOptions, extractCalculatedOutputs} from './limeCalcs'
import {matchAllRows} from './limeSoilUtils'
import {useBoundaries} from '../../controllers/useBoundaries'
import TemplateData from '../../controllers/TemplateData'
import SelectNumberNullable from "../../components/SelectNumberNullable";
import Number from "../../components/Number";

const defaultProductSources: LimeProgramProductSources = {
    calciticLimeProductId: null,
    dolomiticLimeProductId: null,
    magnesiumProductId: null,
    gypsumProductId: null,
    potassiumProductId: null,
    phosphateProductId: null,
    limeGypsumDepthCm: 30,
    pkDepthCm: 30
}

const NewLimeProgram: React.FC = () => {
    const context = useContext(AppContext)
    const navigate = useNavigate()
    const stepperRef = useRef<StepperRef>(null)
    const pagedTableRef = useRef<PagedTableFunctions<ClientPaged>>()

    // Step 1: Program name
    const [name, setName] = useState('')
    const nameInput = useRef<HTMLInputElement>(null)

    // Step 2: Client and farm
    const [client, setClient] = useState<ClientPaged | null>(null)

    // Step 3: Region and Product sources
    const [regions, setRegions] = useState<LimingRegionResponse[]>([])
    const [regionId, setRegionId] = useState<number | null>(null)
    const [productSources, setProductSources] = useState<LimeProgramProductSources>(defaultProductSources)
    const [products, setProducts] = useState<LimeProductResponse[]>([])

    // Step 4: Soil data with auto block matching
    const [lands, setLands] = useState<LandResponse[]>([])
    const [labInputs, setLabInputs] = useState<LimeProgramLabInputs>({rows: []})
    const [showSoilData, setShowSoilData] = useState(false)
    const [selectedLandIds, setSelectedLandIds] = useState<number[]>([])

    // Step 5: Template
    const [templates, setTemplates] = useState<LimeTemplateBasicResult[]>([])
    const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null)
    const [selectedTemplateData, setSelectedTemplateData] = useState<TemplateData | null>(null)

    const selectedRegion = regions.find(r => r.id === regionId)

    // Fetch boundaries for spatial matching
    const allLandIds = React.useMemo(() => lands.map(l => l.id), [lands])
    const {boundaryMap, loading: boundaryLoading} = useBoundaries(allLandIds)

    // Build land name lookup (id -> name) for display
    const landNames = React.useMemo(() => {
        const map = new Map<number, string>()
        for (const land of lands) {
            if (land.name) map.set(land.id, land.name)
        }
        return map
    }, [lands])

    // Auto-match rows to blocks (sets landId + landName for display)
    const matchedLabInputs = React.useMemo(
        () => matchAllRows(labInputs, boundaryMap, landNames),
        [labInputs, boundaryMap, landNames]
    )

    // Derive sample counts from matched rows
    const sampleCountMap = React.useMemo(() => {
        const counts = new Map<number, number>()
        let unmatched = 0
        for (const row of matchedLabInputs.rows) {
            if (row.landId != null) {
                counts.set(row.landId, (counts.get(row.landId) ?? 0) + 1)
            } else {
                unmatched++
            }
        }
        return {counts, unmatched}
    }, [matchedLabInputs])

    function toggleBlock(landId: number) {
        setSelectedLandIds(prev =>
            prev.includes(landId) ? prev.filter(id => id !== landId) : [...prev, landId]
        )
    }

    const labInputUnit = selectedRegion?.labInputUnit

    // Load regions on mount
    React.useEffect(() => {
        LimeController.getRegions().then(data => {
            setRegions(data)
            const first = data[0]
            if (first) setRegionId(first.id)
        })
    }, [])

    // Load products when region changes
    React.useEffect(() => {
        if (!regionId) {
            return
        }

        let cancelled = false

        // Clear existing products so stale options are not shown while loading
        setProducts([])

        wrapLoader(
            context,
            LimeController.getProductsByRegion(regionId),
            (data) => {
                if (!cancelled) {
                    setProducts(data)
                }
            }
        )

        return () => {
            cancelled = true
        }
    }, [regionId])
    function clientClicked(row: ClientPaged) {
        wrapLoader(context, LimeController.getFarmLandsSync(row.farmId), (farmLands) => {
            setLands(farmLands)
            setClient(row)
            setLabInputs({rows: []})
            setSelectedLandIds([])
            stepperRef.current?.next()
        })
    }

    function loadTemplates() {
        if (labInputs.rows.length === 0) {
            context.showSnack(<Failed title={context.word('no_soil_data')}/>)
            return
        }

        wrapLoader(context, LimeController.getApprovedTemplates(), (templatesData) => {
            setTemplates(templatesData)
            stepperRef.current?.next()
        })
    }

    function onRegionChange(newRegionId: number | null) {
        setRegionId(newRegionId)
        // Reset product selections when region changes
        setProductSources(defaultProductSources)
        // Clear lab data since unit may have changed
        setLabInputs({rows: []})
    }

    function generateProgram() {
        if (!client || !selectedTemplateId || !regionId) return

        const hideLoader = context.showLoader()

        // Load template data if not already loaded, then calculate and save
        const templatePromise = selectedTemplateData
            ? Promise.resolve(selectedTemplateData)
            : LimeController.get(selectedTemplateId).then(t => t.data)

        templatePromise.then(templateData => {
            // Calculate rows using the same logic as LimeProgram.tsx
            const findProduct = (id: number | null | undefined) =>
                id ? products.find(p => p.id === id) : undefined

            const opts: CalcOptions = {
                limeGypsumDepthCm: productSources.limeGypsumDepthCm,
                pkDepthCm: productSources.pkDepthCm,
                productSelected: {
                    calcitic: productSources.calciticLimeProductId != null,
                    dolomitic: productSources.dolomiticLimeProductId != null,
                    magnesium: productSources.magnesiumProductId != null,
                    gypsum: productSources.gypsumProductId != null,
                    potassium: productSources.potassiumProductId != null,
                    phosphate: productSources.phosphateProductId != null,
                },
                products: buildProductValues(
                    findProduct(productSources.calciticLimeProductId),
                    findProduct(productSources.dolomiticLimeProductId),
                    findProduct(productSources.magnesiumProductId),
                    findProduct(productSources.gypsumProductId),
                    findProduct(productSources.potassiumProductId),
                    findProduct(productSources.phosphateProductId)
                )
            }

            const calculatedRows = calculateLandRows(matchedLabInputs, templateData, opts)

            // Merge calculated values into lab inputs using nested outputs property
            // Use matchedLabInputs (which has landId set from spatial matching), not raw labInputs
            const labInputsWithCalcs: LimeProgramLabInputs = {
                rows: matchedLabInputs.rows.map((row, idx) => {
                    const calc = calculatedRows[idx]
                    if (!calc) return row
                    return { ...row, outputs: extractCalculatedOutputs(calc) }
                })
            }

            return LimeController.createProgram({
                name,
                farmId: client.farmId,
                regionId,
                limeTemplateId: selectedTemplateId,
                productSources,
                labInputs: labInputsWithCalcs,
                selectedLandIds
            })
        }).then(programId => {
            navigate(`/liming/programs/${programId}`)
        }).catch(() => {
            context.showSnack(<Failed title={context.word('failed_to_create')}/>)
        }).finally(hideLoader)
    }

    function nextStep() {
        stepperRef.current?.next()
        pagedTableRef.current?.focus()
    }

    const calciteProducts = products.filter(p => p.type === LimeProductType.Calcite && p.active)
    const dolomiteProducts = products.filter(p => p.type === LimeProductType.Dolomite && p.active)
    const magnesiumProducts = products.filter(p => p.type === LimeProductType.Magnesium && p.active)
    const gypsumProducts = products.filter(p => p.type === LimeProductType.Gypsum && p.active)
    const potassiumProducts = products.filter(p => p.type === LimeProductType.Potassium && p.active)
    const phosphateProducts = products.filter(p => p.type === LimeProductType.Phosphate && p.active)

    return <div>
        <Dialog
            title={context.word('soil_data')}
            show={showSoilData}
            setShow={setShowSoilData}
            body={
                <LimeSoilDataDialog
                    data={labInputs}
                    onSave={(data) => {
                        setLabInputs(data)
                        setShowSoilData(false)
                        // Auto-select blocks on first upload (when nothing selected yet)
                        if (selectedLandIds.length === 0) {
                            const matched = matchAllRows(data, boundaryMap, landNames)
                            const matchedIds = matched.rows
                                .map(r => r.landId)
                                .filter((id): id is number => id !== null)
                            if (matchedIds.length > 0) {
                                setSelectedLandIds(Array.from(new Set(matchedIds)))
                            }
                        }
                    }}
                    onDiscard={() => setShowSoilData(false)}
                    labInputUnit={labInputUnit}
                    landIds={allLandIds}
                />
            }
        />

        <div className="text-primary text-xl my-2 underline">{context.word('create_lime_program')}.</div>
        <Stepper
            tabMode="toggle"
            ref={stepperRef}
            onChange={(_, toIndex, source) => {
                return !(toIndex === 4 && source === 'click')
            }}
            tabs={[
                {
                    key: 'name',
                    text: context.word('program_name'),
                    onFocus: () => nameInput.current?.focus(),
                    content: () => <div className="p-2">
                        <div className="text-xl underline tracking-wider">1. {context.word('program_name')}</div>
                        <input ref={nameInput} className="input" type="text" autoFocus={true}
                               placeholder={context.word('program_name')} {...bind(name, setName)}
                               onKeyDown={onEnter(nextStep)}/>
                        <div className="text-right my-2">
                            <div className="btn bg-primary" onClick={() => stepperRef.current?.next()}>{context.word('next')}</div>
                        </div>
                    </div>
                },
                {
                    key: 'clientfarm',
                    text: context.word('client_and_farm'),
                    onFocus: () => pagedTableRef.current?.focus(),
                    content: () => <div className="p-2">
                        <div className="text-xl underline tracking-wider">2. {context.word('client_and_farm')}</div>
                        <div>{client ? context.word('you_have_selected') + ': ' + client.clientName + ' - ' + client.farmName : context.word('choose_a_client')}</div>
                        <PagedSearchTable
                            componentRef={pagedTableRef}
                            call={ClientController.search}
                            rowClick={row => clientClicked(row)}
                            columns={[
                                {
                                    header: context.word('client_name'),
                                    row: (item) => item.clientName
                                },
                                {
                                    header: context.word('farm_name'),
                                    row: (item) => item.farmName
                                }
                            ]}
                            keyExtractor={i => i.farmId}/>
                    </div>
                },
                {
                    key: 'sources',
                    text: context.word('product_sources'),
                    content: () => <div className="p-2">
                        <div className="text-xl underline tracking-wider">3. {context.word('product_sources')}</div>

                        {/* Region Selection */}
                        <div className="my-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {context.word('region')}
                            </label>
                            <SelectNumberNullable options={regions} 
                                                  textFunc={r => r.name} 
                                                  valueFunc={r => r.id} 
                                                  value={regionId} 
                                                  onChange={v => onRegionChange(v)}/>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            {/* Calcitic Lime Product */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    {context.word('calcitic_lime_product')}
                                </label>
                                <select
                                    className="input w-full"
                                    value={productSources.calciticLimeProductId || ''}
                                    onChange={e => setProductSources({
                                        ...productSources,
                                        calciticLimeProductId: e.target.value ? parseInt(e.target.value) : null
                                    })}
                                >
                                    <option value="">{context.word('none')}</option>
                                    {calciteProducts.map(p => (
                                        <option key={p.id} value={p.id}>{p.name}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Dolomitic Lime Product */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    {context.word('dolomitic_lime_product')}
                                </label>
                                <select
                                    className="input w-full"
                                    value={productSources.dolomiticLimeProductId || ''}
                                    onChange={e => setProductSources({
                                        ...productSources,
                                        dolomiticLimeProductId: e.target.value ? parseInt(e.target.value) : null
                                    })}
                                >
                                    <option value="">{context.word('none')}</option>
                                    {dolomiteProducts.map(p => (
                                        <option key={p.id} value={p.id}>{p.name}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Magnesium Product */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    {context.word('magnesium_product')}
                                </label>
                                <select
                                    className="input w-full"
                                    value={productSources.magnesiumProductId || ''}
                                    onChange={e => setProductSources({
                                        ...productSources,
                                        magnesiumProductId: e.target.value ? parseInt(e.target.value) : null
                                    })}
                                >
                                    <option value="">{context.word('none')}</option>
                                    {magnesiumProducts.map(p => (
                                        <option key={p.id} value={p.id}>{p.name}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Gypsum Product */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    {context.word('gypsum_product')}
                                </label>
                                <select
                                    className="input w-full"
                                    value={productSources.gypsumProductId || ''}
                                    onChange={e => setProductSources({
                                        ...productSources,
                                        gypsumProductId: e.target.value ? parseInt(e.target.value) : null
                                    })}
                                >
                                    <option value="">{context.word('none')}</option>
                                    {gypsumProducts.map(p => (
                                        <option key={p.id} value={p.id}>{p.name}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Potassium Source */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    {context.word('potassium_source')}
                                </label>
                                <select
                                    className="input w-full"
                                    value={productSources.potassiumProductId ?? ''}
                                    onChange={e => setProductSources({
                                        ...productSources,
                                        potassiumProductId: e.target.value ? parseInt(e.target.value) : null
                                    })}
                                >
                                    <option value="">{context.word('none')}</option>
                                    {potassiumProducts.map(p => (
                                        <option key={p.id} value={p.id}>{p.name}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Phosphate Source */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    {context.word('phosphate_source')}
                                </label>
                                <select
                                    className="input w-full"
                                    value={productSources.phosphateProductId ?? ''}
                                    onChange={e => setProductSources({
                                        ...productSources,
                                        phosphateProductId: e.target.value ? parseInt(e.target.value) : null
                                    })}
                                >
                                    <option value="">{context.word('none')}</option>
                                    {phosphateProducts.map(p => (
                                        <option key={p.id} value={p.id}>{p.name}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Lime & Gypsum Application Depth */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    {context.word('lime_gypsum_depth')} (cm)
                                </label>
                                <Number
                                    value={productSources.limeGypsumDepthCm}
                                    change={v => setProductSources({
                                        ...productSources,
                                        limeGypsumDepthCm: v
                                    })}
                                />
                            </div>

                            {/* P & K Application Depth */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    {context.word('pk_depth')} (cm)
                                </label>
                                <Number
                                    value={productSources.pkDepthCm}
                                    change={v => setProductSources({
                                        ...productSources,
                                        pkDepthCm: v
                                    })}
                                />
                            </div>
                        </div>

                        <div className="text-right my-4">
                            <div className="btn bg-primary" onClick={() => stepperRef.current?.next()}>{context.word('next')}</div>
                        </div>
                    </div>
                },
                {
                    key: 'field',
                    text: context.word('soil_data'),
                    content: () => <div className="p-2">
                        <div className="text-xl underline tracking-wider">4. {context.word('soil_data')}</div>

                        <div className="mt-4 flex items-center gap-4">
                            <div className="text-sm text-gray-600">
                                {context.word('soil_data')}: {labInputs.rows.length} {context.word('rows')}
                            </div>
                            <div
                                className="btn bg-primary btn-sm cursor-pointer"
                                onClick={() => setShowSoilData(true)}
                            >
                                {context.word('edit_soil_data')}
                            </div>
                        </div>

                        {lands.length > 0 && labInputs.rows.length > 0 && (
                            <div className="mt-4">
                                {boundaryLoading && (
                                    <div className="text-sm text-gray-500 mb-2">{context.word('loading')}...</div>
                                )}
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b text-left text-gray-600">
                                            <th className="p-2 w-10"></th>
                                            <th className="p-2">{context.word('block_name')}</th>
                                            <th className="p-2 text-right">{context.word('samples')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sampleCountMap.unmatched > 0 && (
                                            <tr className="border-b opacity-50">
                                                <td className="p-2"></td>
                                                <td className="p-2 italic text-gray-500">{context.word('unmatched')}</td>
                                                <td className="p-2 text-right">{sampleCountMap.unmatched}</td>
                                            </tr>
                                        )}
                                        {lands.map(land => {
                                            const isSelected = selectedLandIds.includes(land.id)
                                            const count = sampleCountMap.counts.get(land.id) ?? 0
                                            return (
                                                <tr
                                                    key={land.id}
                                                    className={`border-b cursor-pointer hover:bg-gray-50 ${isSelected ? '' : 'opacity-50'}`}
                                                    onClick={() => toggleBlock(land.id)}
                                                >
                                                    <td className="p-2">
                                                        <CheckBox value={isSelected} onChange={() => toggleBlock(land.id)}/>
                                                    </td>
                                                    <td className="p-2 font-medium">{land.name}</td>
                                                    <td className="p-2 text-right">{count}</td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {labInputs.rows.length > 0 && (
                            <div className="text-right my-4">
                                <div className="btn bg-primary" onClick={loadTemplates}>{context.word('next')}</div>
                            </div>
                        )}
                    </div>
                },
                {
                    key: 'template',
                    text: context.word('template'),
                    content: () => <div className="p-2">
                        <div className="text-xl underline tracking-wider">5. {context.word('template')}</div>
                        <p className="text-sm text-gray-600 mb-4">{context.word('select_lime_template')}</p>

                        <div className="space-y-2">
                            {templates.map(template => (
                                <div
                                    key={template.id}
                                    className={`border rounded p-3 cursor-pointer ${selectedTemplateId === template.id ? 'border-primary bg-primary-50' : 'hover:bg-gray-50'}`}
                                    onClick={() => setSelectedTemplateId(template.id)}
                                >
                                    <div className="font-medium">{template.name}</div>
                                    {template.description && (
                                        <div className="text-sm text-gray-500">{template.description}</div>
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className="text-right my-4">
                            <div className="btn bg-primary" onClick={generateProgram}>{context.word('generate')}</div>
                        </div>
                    </div>
                }
            ]}/>
    </div>
}

export default NewLimeProgram
